import { type RefObject } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import type { AudioLoadingState } from "../AudioPlayerContext";

export type EngineCallbacks = {
  /** The speed to start a loaded track at. */
  rate: () => number;
  onTime: (audio: HTMLAudioElement) => void;
  onLoaded: () => void;
  onEnded: () => void;
  onError: () => void;
};

export type Engine = {
  audioRef: RefObject<HTMLAudioElement | null>;
  isPlaying: boolean;
  loadingState: AudioLoadingState;
  currentTime: number;
  duration: number;
  /** Starts loading a track, which plays as soon as it can. */
  load: (url: string) => void;
  /** Plays what is loaded, at `rate`. */
  resume: (rate: number) => void;
  pause: () => void;
  seek: (time: number) => void;
  skip: (seconds: number) => void;
  setRate: (rate: number) => void;
};

/** An interrupted play() is not a failure. */
export const startPlaying = (audio: HTMLAudioElement, rate: number): void => {
  audio.playbackRate = rate;
  audio.play().catch((error: unknown) => {
    if ((error as { name?: string }).name !== "AbortError") {
      console.error("Failed to play audio:", error);
    }
  });
};

const skipBy = (audio: HTMLAudioElement, seconds: number): void => {
  audio.currentTime = Math.min(audio.duration || 0, Math.max(0, audio.currentTime + seconds));
};

type Setters = {
  setIsPlaying: (value: boolean) => void;
  setLoadingState: (value: AudioLoadingState) => void;
  setCurrentTime: (value: number) => void;
  setDuration: (value: number) => void;
};

const bind = (audio: HTMLAudioElement, set: Setters, callbacks: { current: EngineCallbacks }): (() => void) => {
  const listeners: Record<string, () => void> = {
    timeupdate: () => {
      set.setCurrentTime(audio.currentTime);
      callbacks.current.onTime(audio);
    },
    loadedmetadata: () => {
      set.setDuration(audio.duration);
      set.setLoadingState("loaded");
      callbacks.current.onLoaded();
      startPlaying(audio, callbacks.current.rate());
    },
    ended: () => {
      set.setIsPlaying(false);
      callbacks.current.onEnded();
    },
    error: () => {
      set.setLoadingState("error");
      set.setIsPlaying(false);
      callbacks.current.onError();
    },
    pause: () => set.setIsPlaying(false),
    play: () => set.setIsPlaying(true),
  };
  for (const [name, listener] of Object.entries(listeners)) {
    audio.addEventListener(name, listener);
  }
  return () => {
    for (const [name, listener] of Object.entries(listeners)) {
      audio.removeEventListener(name, listener);
    }
  };
};

const lifecycle = (audioRef: { current: HTMLAudioElement | null }, set: Setters, latest: { current: EngineCallbacks }) => () => {
  const audio = new Audio();
  audio.preload = "none";
  audioRef.current = audio;
  const unbind = bind(audio, set, latest);
  return () => {
    unbind();
    audio.pause();
    audio.removeAttribute("src");
    audioRef.current = null;
  };
};

const useEngineState = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [loadingState, setLoadingState] = useState<AudioLoadingState>("idle");
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  return {
    values: { isPlaying, loadingState, currentTime, duration },
    set: { setIsPlaying, setLoadingState, setCurrentTime, setDuration },
  };
};

/** The one `<audio>` element of the site, made in the browser, with its state as the player shows it. */
export const useAudioEngine = (callbacks: EngineCallbacks): Engine => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const latest = useRef(callbacks);
  latest.current = callbacks;
  const { values, set } = useEngineState();
  useEffect(lifecycle(audioRef, set, latest), []);
  const act = (run: (audio: HTMLAudioElement) => void) => audioRef.current && run(audioRef.current);

  return {
    ...values,
    audioRef,
    load: (url) =>
      act((audio) => {
        set.setLoadingState("loading");
        set.setCurrentTime(0);
        set.setDuration(0);
        audio.src = url;
        audio.load();
      }),
    seek: (time) =>
      act((audio) => {
        audio.currentTime = time;
        set.setCurrentTime(time);
      }),
    resume: (rate) => act((audio) => startPlaying(audio, rate)),
    pause: () => act((audio) => audio.pause()),
    skip: (seconds) => act((audio) => skipBy(audio, seconds)),
    setRate: (rate) => act((audio) => Object.assign(audio, { playbackRate: rate })),
  };
};
